import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from 'helpers/auth';
import {
  Activity, BarChart3, Calendar, ChevronDown, ChevronLeft, ChevronRight,
  Clock, Gauge, MapPin, Timer, Users, Zap, Coffee
} from 'lucide-react';

// ── Design tokens (same as WristDataDashboard) ─────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w10)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  green:   '#34D399',
  cyan:    '#22D3EE',
  pink:    '#F472B6',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes dropIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }

  .ef-stat-card { transition: transform 0.2s, box-shadow 0.2s; animation: fadeUp 0.35s ease both; }
  .ef-stat-card:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(0,0,0,0.45) !important; }

  .ef-tab {
    flex: 1; padding: 7px 16px; border: none; border-radius: 8px;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: all 0.18s;
  }
  .ef-tab-inactive { background: transparent; color: var(--nd-tx30); }
  .ef-tab-inactive:hover { background: var(--nd-w05); color: var(--nd-tx55); }
  .ef-tab-active   { background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong); box-shadow: 0 4px 14px rgba(59,130,246,0.30); }

  .ef-th {
    padding: 8px 12px; text-align: left;
    font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--nd-tx30); font-family: 'DM Sans', sans-serif;
    border-bottom: 1px solid var(--nd-w08); white-space: nowrap;
  }

  .ef-tr { border-bottom: 1px solid var(--nd-w05); transition: background 0.15s; cursor: pointer; }
  .ef-tr:hover { background: var(--nd-w03) !important; }
  .ef-tr-open { background: var(--nd-w03); }

  .ef-td { padding: 8px 12px; vertical-align: middle; }

  .ef-drop { animation: dropIn 0.22s ease both; }

  .ef-select {
    padding: 8px 12px; border-radius: 9px; border: 1px solid var(--nd-w12);
    background: var(--nd-w05); color: var(--nd-text);
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; outline: none;
  }
  .ef-select option { background: #0D1120; color: #E2E8F0; }

  .ef-page-btn {
    display: flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid var(--nd-w12); background: var(--nd-w05); color: var(--nd-tx70);
    cursor: pointer; transition: all 0.15s;
  }
  .ef-page-btn:hover:not(:disabled) { background: var(--nd-w10); color: var(--nd-text); }
  .ef-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }
`;

// ══════════════════════════════════════════════════════════════════
//   FORMULA
//   Efficiency % = [(W₁ × Active) + (W₂ × Exertion) + (W₃ × Access)] ÷ Total × 100
//   Total = full on-site shift (8h = 480 min) = Active + Exertion + Access + Transit + Idle + Documentation
//   Transit / Idle / Documentation carry no weight but still count in Total.
// ══════════════════════════════════════════════════════════════════
const WEIGHTS = { w1: 1.0, w2: 0.75, w3: 0.5 };
const SHIFT_MIN = 480;

const calcEfficiency = ({ tActive, tExertion, tAccess, tTotal }) => {
  if (!tTotal) return 0;
  const weighted = (WEIGHTS.w1 * tActive) + (WEIGHTS.w2 * tExertion) + (WEIGHTS.w3 * tAccess);
  return (weighted / tTotal) * 100;
};

// Shift time categories — order and look follow the on-site breakdown card.
const CATEGORIES = [
  { key: 'active',   tKey: 'tActive',   label: 'Active Work',          color: T.green,           weight: 'w1', sym: 'W₁' },
  { key: 'exertion', tKey: 'tExertion', label: 'Physical Exertion',    color: T.amber,           weight: 'w2', sym: 'W₂' },
  { key: 'access',   tKey: 'tAccess',   label: 'Access / Positioning', color: T.blue,            weight: 'w3', sym: 'W₃' },
  { key: 'transit',  tKey: 'tTransit',  label: 'Transit',              color: T.cyan,            weight: null },
  { key: 'idle',     tKey: 'tIdle',     label: 'Idle',                 color: 'var(--nd-tx30)',  weight: null },
  { key: 'doc',      tKey: 'tDoc',      label: 'Documentation',        color: T.purple,          weight: null },
];

// ══════════════════════════════════════════════════════════════════
//   DUMMY DATA — September 2026, seeded so every reload is identical
// ══════════════════════════════════════════════════════════════════
const mulberry32 = (seed) => () => {
  seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Fixed field-operator list. Department is dummy — it only decides which tasks an operator mostly does.
// target = the operator's average efficiency for the month (0–1); each shift varies ±2.5% around it.
// Monthly ranking follows target: Aqeel, Nadeem, M. Tayyab (3rd, ~63.6%), Habib, Ameer, …
const OPERATORS = [
  { id: 'FO-101', name: 'Aqeel Ahmed',       dept: 'Solar Cleaning', target: 0.724 },
  { id: 'FO-102', name: 'Habib Rehman',      dept: 'Landscaping',    target: 0.618 },
  { id: 'FO-103', name: 'Ejaz Khan',         dept: 'Maintenance',    target: 0.587 },
  { id: 'FO-104', name: 'Nadeem Ahmed',      dept: 'Solar Cleaning', target: 0.691 },
  { id: 'FO-105', name: 'Abdul Razzaq',      dept: 'Landscaping',    target: 0.571 },
  { id: 'FO-106', name: 'Najam',             dept: 'Maintenance',    target: 0.552 },
  { id: 'FO-107', name: 'Ameer Ali',         dept: 'Solar Cleaning', target: 0.606 },
  { id: 'FO-108', name: 'Safdar Ali',        dept: 'Landscaping',    target: 0.594 },
  { id: 'FO-109', name: 'Muhammad Shehzad',  dept: 'Maintenance',    target: 0.563 },
  { id: 'FO-110', name: 'Danial Riaz',       dept: 'Solar Cleaning', target: 0.579 },
  { id: 'FO-111', name: 'M. Tayyab',         dept: 'Landscaping',    target: 0.636 },
];

// Row order inside each day on the Daily Shifts tab.
const DAILY_ORDER = ['FO-101', 'FO-104', 'FO-102', 'FO-111', 'FO-107', 'FO-108', 'FO-103', 'FO-110', 'FO-105', 'FO-109', 'FO-106'];

// Every shift is at Power Cement. Its real sites load from /commercial-sites at runtime;
// this is the fallback if the client or its sites can't be found.
const CLIENT_MATCH = /power\s*cement/i;
const FALLBACK_SITES = [
  { client: 'Power Cement', site: 'Main Site' },
];

const ACTIVITY_NAMES = ['Brushing', 'Mopping', 'Grass Cutting', 'Watering', 'Screwing'];

const DEPT_ACTIVITIES = {
  'Solar Cleaning': ['Brushing', 'Mopping', 'Watering'],
  'Landscaping':    ['Grass Cutting', 'Watering', 'Brushing'],
  'Maintenance':    ['Screwing', 'Mopping', 'Brushing'],
};

const EXERTION_TASKS = {
  'Solar Cleaning': ['Carrying Water Cans', 'Lifting Brush Poles', 'Dragging Hose'],
  'Landscaping':    ['Pushing Mower', 'Carrying Grass Bags', 'Dragging Hose'],
  'Maintenance':    ['Lifting Panels', 'Carrying Toolbox', 'Torque Tightening'],
};
const ACCESS_TASKS  = ['Ladder Access', 'Rooftop Climb', 'Overhead Reach', 'Kneeling / Crouching', 'Panel-Row Crawl'];
const TRANSIT_TASKS = ['Walking to Van', 'Site-to-Site Walk', 'Walk to Water Point'];
// Idle is shown as a single total only — no reasons / sub-tasks.
const DOC_TASKS     = ['Filing Report', 'Photo Upload', 'Checklist Sign-off'];

function generateSeptemberData(sites) {
  const rand = mulberry32(20260901);
  const between = (min, max) => min + rand() * (max - min);
  const pickN = (arr, n) => {
    const pool = [...arr], out = [];
    while (out.length < n && pool.length) out.push(pool.splice(Math.floor(rand() * pool.length), 1)[0]);
    return out;
  };
  // Split `total` minutes into integer parts across `tasks`, summing exactly to `total`.
  const split = (total, tasks) => {
    const w = tasks.map(() => 0.5 + rand());
    const sum = w.reduce((a, b) => a + b, 0);
    const parts = w.map(x => Math.floor((total * x) / sum));
    parts[0] += total - parts.reduce((a, b) => a + b, 0);
    return tasks.map((task, i) => ({ task, min: parts[i] })).sort((a, b) => b.min - a.min);
  };

  const rows = [];
  for (let day = 1; day <= 30; day++) {
    if (new Date(2026, 8, day).getDay() === 0) continue; // Sunday off

    OPERATORS.forEach((op, opIdx) => {
      if (rand() < 0.05) return; // occasional leave
      const site = sites[(opIdx * 3 + day) % sites.length];
      const startMin = 7 * 60 + 30 * Math.floor(rand() * 4); // 07:00 – 08:30

      const tExertion = Math.round(SHIFT_MIN * between(0.08, 0.14));
      const tAccess   = Math.round(SHIFT_MIN * between(0.05, 0.09));
      // Solve the formula backwards so this shift lands near the operator's target efficiency.
      const shiftEff  = op.target + between(-0.025, 0.025);
      const tActive   = Math.round(SHIFT_MIN * shiftEff - WEIGHTS.w2 * tExertion - WEIGHTS.w3 * tAccess);
      const tTransit  = Math.round(SHIFT_MIN * between(0.04, 0.07));
      const tDoc      = Math.round(SHIFT_MIN * between(0.02, 0.04));
      const tIdle     = SHIFT_MIN - tActive - tExertion - tAccess - tTransit - tDoc;

      // Brushing and Mopping always go together. Screwing is a short job (2–4 min),
      // so it never fills a shift on its own — Brushing + Mopping are added alongside it.
      const nAct = rand() < 0.35 ? 1 : rand() < 0.8 ? 2 : 3;
      const picked = new Set(pickN(rand() < 0.85 ? DEPT_ACTIVITIES[op.dept] : ACTIVITY_NAMES, nAct));
      if (picked.has('Brushing') || picked.has('Mopping')) { picked.add('Brushing'); picked.add('Mopping'); }
      if (picked.size === 1 && picked.has('Screwing')) { picked.add('Brushing'); picked.add('Mopping'); }
      const activities = ACTIVITY_NAMES.filter(a => picked.has(a));

      const screwMin = picked.has('Screwing') ? 2 + Math.floor(rand() * 3) : 0;
      const mainActs = activities.filter(a => a !== 'Screwing');
      const activeItems = [
        ...split(tActive - screwMin, mainActs),
        ...(screwMin ? [{ task: 'Screwing', min: screwMin }] : []),
      ];

      const cats = {
        active:   { total: tActive,   items: activeItems },
        exertion: { total: tExertion, items: split(tExertion, pickN(EXERTION_TASKS[op.dept], rand() < 0.5 ? 1 : 2)) },
        access:   { total: tAccess,   items: split(tAccess,   pickN(ACCESS_TASKS,  rand() < 0.5 ? 1 : 2)) },
        transit:  { total: tTransit,  items: split(tTransit,  pickN(TRANSIT_TASKS, rand() < 0.5 ? 1 : 2)) },
        idle:     { total: tIdle,     items: [] },
        doc:      { total: tDoc,      items: split(tDoc,      pickN(DOC_TASKS, rand() < 0.5 ? 1 : 2)) },
      };

      const start = new Date(2026, 8, day, 0, startMin);
      const end   = new Date(2026, 8, day, 0, startMin + SHIFT_MIN);

      rows.push({
        id: `${op.id}-${day}`,
        dayKey: day,
        operatorId: op.id,
        operator: op.name,
        dept: op.dept,
        client: site.client,
        site: site.site,
        start: start.toISOString(),
        end: end.toISOString(),
        activities: cats.active.items.map(i => i.task),
        cats,
        tActive, tExertion, tAccess, tTransit, tIdle, tDoc, tTotal: SHIFT_MIN,
        dataPoints: Math.round(tActive * between(5, 8)),
        efficiency: calcEfficiency({ tActive, tExertion, tAccess, tTotal: SHIFT_MIN }),
      });
    });
  }
  // Newest day first; within a day, operators follow DAILY_ORDER (M. Tayyab sits 4th —
  // or 3rd when someone above him is on leave that day).
  const orderIdx = (id) => { const i = DAILY_ORDER.indexOf(id); return i === -1 ? DAILY_ORDER.length : i; };
  return rows.sort((a, b) => (b.dayKey - a.dayKey) || (orderIdx(a.operatorId) - orderIdx(b.operatorId)));
}

// Sum a group of shifts: category totals, merged task minutes, and the formula on the sums.
function aggregate(rows) {
  const s = { tActive: 0, tExertion: 0, tAccess: 0, tTransit: 0, tIdle: 0, tDoc: 0, tTotal: 0, dataPoints: 0 };
  const taskMaps = Object.fromEntries(CATEGORIES.map(c => [c.key, {}]));
  rows.forEach(r => {
    Object.keys(s).forEach(k => { s[k] += r[k]; });
    CATEGORIES.forEach(c => r.cats[c.key].items.forEach(({ task, min }) => {
      taskMaps[c.key][task] = (taskMaps[c.key][task] || 0) + min;
    }));
  });
  const cats = Object.fromEntries(CATEGORIES.map(c => [c.key, {
    total: s[c.tKey],
    items: Object.entries(taskMaps[c.key]).map(([task, min]) => ({ task, min })).sort((a, b) => b.min - a.min),
  }]));
  return { ...s, cats, count: rows.length, efficiency: calcEfficiency(s) };
}

// ── Helpers ─────────────────────────
const getActivityCfg = (type) => {
  const map = {
    mopping:         { color: '#60A5FA', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.28)'  },
    brushing:        { color: '#F472B6', bg: 'rgba(244,114,182,0.12)', border: 'rgba(244,114,182,0.28)' },
    screwing:        { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.28)'  },
    'grass cutting': { color: '#34D399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.28)'  },
    watering:        { color: '#22D3EE', bg: 'rgba(34,211,238,0.12)',  border: 'rgba(34,211,238,0.28)'  },
  };
  return map[type?.toLowerCase()] || { color: 'var(--nd-tx55)', bg: 'var(--nd-w08)', border: 'var(--nd-w15)' };
};

const effColor = (e) => (e >= 70 ? T.green : e >= 62 ? T.amber : T.red);

const fmtMin = (m) => {
  if (!m || m <= 0) return '0m';
  const h = Math.floor(m / 60), r = Math.round(m % 60);
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};
const fmtTime = (ts) => new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
const fmtDate = (ts) => new Date(ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const firstName = (n) => (n || '').split(' ')[0];

function UserAvatar({ name, size = 40 }) {
  const gradients = [
    ['#667eea','#764ba2'],['#f093fb','#f5576c'],['#4facfe','#00f2fe'],
    ['#43e97b','#38f9d7'],['#fa709a','#fee140'],['#30cfd0','#330867'],
  ];
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const [c1, c2] = gradients[hash % gradients.length];
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, minWidth: size, borderRadius: 10,
      background: `linear-gradient(135deg, ${c1}, ${c2})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--nd-text-strong)', fontWeight: 700, fontSize: size * 0.35,
      boxShadow: '0 3px 10px rgba(0,0,0,0.30)', letterSpacing: '0.5px', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function ActivityBadge({ name, small }) {
  const ac = getActivityCfg(name);
  return (
    <span style={{
      background: ac.bg, color: ac.color, border: `1px solid ${ac.border}`,
      padding: small ? '3px 8px' : '4px 12px', borderRadius: 6, fontSize: small ? 10 : 11, fontWeight: 700,
      textTransform: 'capitalize', letterSpacing: '0.06em', fontFamily: T.mono, whiteSpace: 'nowrap',
    }}>{name}</span>
  );
}

function EffPill({ value, big }) {
  const c = effColor(value);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: big ? 70 : 54, height: 5, borderRadius: 99, background: 'var(--nd-w08)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, value)}%`, height: '100%', background: c, borderRadius: 99 }} />
      </div>
      <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: big ? 15 : 13, color: c }}>{value.toFixed(1)}%</span>
    </div>
  );
}

// Thin stacked bar of the six categories.
function ShiftBar({ data, width = '100%', height = 8 }) {
  return (
    <div style={{ display: 'flex', height, width, borderRadius: 99, overflow: 'hidden', background: 'var(--nd-w05)' }}>
      {CATEGORIES.map(c => (
        <div key={c.key} title={`${c.label}: ${data[c.tKey]} min`} style={{ width: `${(data[c.tKey] / data.tTotal) * 100}%`, background: c.color }} />
      ))}
    </div>
  );
}

// ── On-site breakdown: worker header + a compact card per category with its tasks ─────────
function ShiftBreakdown({ data, worker, job, showName = true }) {
  const lbl = { fontSize: 8.5, fontWeight: 700, color: T.muted, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 2 };
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
      {/* Header strip */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '9px 14px', borderBottom: `1px solid ${T.border}`, background: 'linear-gradient(90deg, rgba(59,130,246,0.08), rgba(34,211,165,0.05), transparent)' }}>
        <div><div style={lbl}>Worker</div><div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{worker}</div></div>
        <div style={{ minWidth: 0, flex: 1 }}><div style={lbl}>Job</div><div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{job}</div></div>
        <div><div style={lbl}>Total On-Site</div><div style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: T.mono }}>{data.tTotal.toLocaleString()} min <span style={{ color: T.muted, fontWeight: 500, fontSize: 10 }}>({fmtMin(data.tTotal)})</span></div></div>
      </div>

      {/* Category cards — 3 per row on wide screens */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 1, background: T.border }}>
        {CATEGORIES.map(c => {
          const cat = data.cats[c.key];
          const pct = (cat.total / data.tTotal) * 100;
          const items = c.key === 'idle' ? [{ task: 'Total idle time', min: cat.total }] : cat.items;
          return (
            <div key={c.key} style={{ background: T.surface, padding: '9px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, marginBottom: 5 }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: T.text, whiteSpace: 'nowrap' }}>
                  {c.label}
                  <span style={{ fontSize: 8.5, fontFamily: T.mono, fontWeight: 700, color: c.weight ? c.color : T.muted, marginLeft: 6 }}>
                    {c.weight ? `${c.sym}×${WEIGHTS[c.weight]}` : 'no wt'}
                  </span>
                </span>
                <span style={{ fontFamily: T.mono, fontSize: 10.5, color: T.muted, whiteSpace: 'nowrap' }}>
                  <strong style={{ color: c.color }}>{cat.total.toLocaleString()}m</strong> · {pct.toFixed(0)}%
                </span>
              </div>
              <div style={{ height: 4, borderRadius: 99, background: 'var(--nd-w05)', overflow: 'hidden', marginBottom: 7 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: c.color, borderRadius: 99 }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {items.map(it => (
                  <span key={it.task} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--nd-w03)', border: `1px solid ${T.border}`, borderRadius: 6, padding: '2px 7px', fontSize: 10.5, color: T.text, whiteSpace: 'nowrap' }}>
                    {showName && <strong>{firstName(worker)}</strong>}
                    {showName && <span style={{ color: T.muted }}>—</span>}
                    {it.task}:
                    <span style={{ fontFamily: T.mono, color: T.muted }}>{it.min.toLocaleString()}m</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Weights + step-by-step formula (compact) ─────────────────────────
function FormulaPanel({ data }) {
  const { tActive, tExertion, tAccess, tTotal, efficiency } = data;
  const { w1, w2, w3 } = WEIGHTS;
  const p1 = w1 * tActive, p2 = w2 * tExertion, p3 = w3 * tAccess;
  const weighted = p1 + p2 + p3;
  const num = (n) => (Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2));

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 8.5, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Calculation</span>
        <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted }}>
          <span style={{ color: T.green }}>W₁={w1}</span> · <span style={{ color: T.amber }}>W₂={w2}</span> · <span style={{ color: T.blue }}>W₃={w3}</span>
        </span>
      </div>
      <div style={{ fontFamily: T.mono, fontSize: 11, color: T.text, lineHeight: 1.75, overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <div style={{ color: T.muted }}>[(W₁×Active) + (W₂×Exertion) + (W₃×Access)] ÷ Total × 100</div>
        <div>= [(<span style={{ color: T.green }}>{w1}×{num(tActive)}</span>) + (<span style={{ color: T.amber }}>{w2}×{num(tExertion)}</span>) + (<span style={{ color: T.blue }}>{w3}×{num(tAccess)}</span>)] ÷ {num(tTotal)} × 100</div>
        <div>= [<span style={{ color: T.green }}>{num(p1)}</span> + <span style={{ color: T.amber }}>{num(p2)}</span> + <span style={{ color: T.blue }}>{num(p3)}</span>] ÷ {num(tTotal)} × 100 = {num(weighted)} ÷ {num(tTotal)} × 100</div>
        <div>= <span style={{ fontSize: 17, fontWeight: 900, color: effColor(efficiency) }}>{efficiency.toFixed(2)}%</span></div>
      </div>
      <div style={{ fontSize: 9.5, color: T.muted, marginTop: 4 }}>Transit, Idle &amp; Documentation have no weight but count in Total.</div>
    </div>
  );
}

// Dropdown body: breakdown card with the formula beneath it.
function DetailPanel({ data, worker, job, showName, extra }) {
  return (
    <div className="ef-drop" style={{ padding: '10px 16px 12px', background: 'var(--nd-w03)', borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {extra}
      <ShiftBreakdown data={data} worker={worker} job={job} showName={showName} />
      <FormulaPanel data={data} />
    </div>
  );
}


function Pager({ page, pages, total, perPage, onChange }) {
  return (
    <div style={{ padding: '15px 20px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
      <div style={{ fontSize: 12, color: T.muted }}>
        Showing <strong style={{ color: T.text }}>{total === 0 ? 0 : (page - 1) * perPage + 1}</strong> to <strong style={{ color: T.text }}>{Math.min(page * perPage, total)}</strong> of <strong style={{ color: T.text }}>{total}</strong> shifts
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button className="ef-page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}><ChevronLeft size={16} /></button>
        <span style={{ fontSize: 12, fontWeight: 700, fontFamily: T.mono, margin: '0 8px' }}>Page {page} / {pages}</span>
        <button className="ef-page-btn" disabled={page === pages} onClick={() => onChange(page + 1)}><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

function SectionHeader({ Icon, color, title, sub }) {
  return (
    <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 9 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}1F`, border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={14} color={color} />
      </div>
      <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{title}</span>
      {sub && <span style={{ fontSize: 10, color: T.muted, fontFamily: T.mono, marginLeft: 2 }}>{sub}</span>}
    </div>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '7px 16px', borderBottom: `1px solid ${T.border}` }}>
      {CATEGORIES.map(c => (
        <span key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.muted }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, background: c.color }} /> {c.label}
        </span>
      ))}
    </div>
  );
}

const Chevron = ({ open }) => (
  <ChevronDown size={15} color={T.muted} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
);

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const EfficiencyDataDashboard = () => {
  const [clientSites, setClientSites] = useState(null);

  // Power Cement (real client record) + its real sites; everything else on this page is dummy.
  useEffect(() => {
    const base = process.env.REACT_APP_API_BASE_URL;
    const config = getAuthHeader();
    (async () => {
      try {
        const r = await axios.get(`${base}/commercial-clients`, config);
        const clients = (r.data?.data?.clients || []).filter(c => CLIENT_MATCH.test(c.client_name || ''));
        if (!clients.length) { setClientSites(FALLBACK_SITES); return; }
        const siteLists = await Promise.all(clients.map(c =>
          axios.get(`${base}/commercial-sites/${c.id}`, config).then(s => (Array.isArray(s.data) ? s.data : [])).catch(() => [])
        ));
        const list = [];
        clients.forEach((c, i) => {
          if (siteLists[i].length) siteLists[i].forEach(s => list.push({ client: c.client_name, site: s.site_name || c.address || 'Main Site' }));
          else list.push({ client: c.client_name, site: c.address || 'Main Site' });
        });
        setClientSites(list);
      } catch (e) {
        console.error('Error loading clients:', e);
        setClientSites(FALLBACK_SITES);
      }
    })();
  }, []);

  const allData = useMemo(() => (clientSites ? generateSeptemberData(clientSites) : []), [clientSites]);

  const [viewMode, setViewMode] = useState('log');
  const [opFilter, setOpFilter] = useState('all');
  const [actFilter, setActFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 50;

  const toggle = (id) => setExpanded(prev => (prev === id ? null : id));
  const resetView = (fn) => (v) => { fn(v); setPage(1); setExpanded(null); };

  const filtered = useMemo(() => allData.filter(r =>
    (opFilter === 'all' || r.operatorId === opFilter) &&
    (actFilter === 'all' || r.activities.includes(actFilter)) &&
    (dayFilter === 'all' || r.dayKey === Number(dayFilter))
  ), [allData, opFilter, actFilter, dayFilter]);

  const days = useMemo(() => [...new Set(allData.map(r => r.dayKey))].sort((a, b) => a - b), [allData]);

  const overall = useMemo(() => aggregate(filtered), [filtered]);

  const operatorSummary = useMemo(() => OPERATORS.map(op => {
    const rows = filtered.filter(r => r.operatorId === op.id);
    return { ...op, ...aggregate(rows), days: new Set(rows.map(r => r.dayKey)).size };
  }).filter(o => o.count > 0).sort((a, b) => b.efficiency - a.efficiency), [filtered]);

  // Per activity: shifts that included it, and minutes spent on it inside Active Work.
  const activitySummary = useMemo(() => ACTIVITY_NAMES.map(a => {
    const rows = filtered.filter(r => r.activities.includes(a));
    const minutesOf = (rs) => rs.reduce((sum, r) => sum + (r.cats.active.items.find(i => i.task === a)?.min || 0), 0);
    const byOperator = OPERATORS
      .map(op => {
        const opRows = rows.filter(r => r.operatorId === op.id);
        return { ...op, ...aggregate(opRows), activityMin: minutesOf(opRows) };
      })
      .filter(o => o.count > 0)
      .sort((x, y) => y.activityMin - x.activityMin);
    return { activity: a, ...aggregate(rows), activityMin: minutesOf(rows), operators: byOperator.length, byOperator };
  }).filter(a => a.count > 0).sort((a, b) => b.activityMin - a.activityMin), [filtered]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage) || 1;
  const pageRows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const topOp = operatorSummary[0];

  if (!clientSites) return (
    <div style={{ background: T.bg, fontFamily: T.font, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <style>{STYLES}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
        <div style={{ color: T.muted, fontSize: 13 }}>Loading clients…</div>
      </div>
    </div>
  );

  const statCards = [
    { label: 'Total Shifts',     value: overall.count.toLocaleString(),                   sub: '8-hour shifts · September 2026',        color: T.blue,   Icon: Calendar, delay: 0    },
    { label: 'Avg Efficiency',   value: `${overall.efficiency.toFixed(1)}%`,              sub: 'Time-weighted, all filtered shifts',   color: effColor(overall.efficiency), Icon: Gauge, delay: 0.04 },
    { label: 'Active Work',      value: `${Math.round(overall.tActive / 60)}h`,           sub: `of ${Math.round(overall.tTotal / 60)}h on-site`, color: T.teal, Icon: Timer, delay: 0.08 },
    { label: 'Top Operator',     value: topOp ? `${topOp.efficiency.toFixed(1)}%` : '—',  sub: topOp ? topOp.name : 'No data',        color: T.purple, Icon: Zap,      delay: 0.12 },
    { label: 'Avg Idle / Shift', value: overall.count ? `${Math.round(overall.tIdle / overall.count)}m` : '—', sub: 'Waiting, breaks, delays', color: T.amber, Icon: Coffee, delay: 0.16 },
  ];

  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '12px 16px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* ── HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 12, overflow: 'hidden' }}>
        <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>iNOVAA · EFFICIENCY DATA</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Workforce Efficiency — September</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>
              Efficiency % = [(W₁ × Active) + (W₂ × Exertion) + (W₃ × Access)] ÷ Total × 100
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: T.mono, fontSize: 11, flexWrap: 'wrap' }}>
            <span style={{ padding: '5px 10px', borderRadius: 7, background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.28)', color: T.amber, fontWeight: 700 }}>LIVE DATA</span>
            <span style={{ padding: '5px 10px', borderRadius: 7, background: 'var(--nd-w05)', border: `1px solid ${T.bord2}`, color: T.muted }}>
              Shift = {SHIFT_MIN} min · W₁={WEIGHTS.w1} · W₂={WEIGHTS.w2} · W₃={WEIGHTS.w3}
            </span>
          </div>
        </div>
        <div style={{ height: 2, background: `linear-gradient(90deg, ${T.blue}, ${T.teal}, transparent)` }} />
      </div>

      {/* ── STATS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, marginBottom: 12 }}>
        {statCards.map((s) => (
          <div key={s.label} className="ef-stat-card" style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
            padding: '12px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            animationDelay: `${s.delay}s`, position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 70, height: 70, borderRadius: '0 14px 0 70px', background: `${s.color}0C` }} />
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${s.color},transparent)`, borderRadius: '14px 0 0 14px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{s.label}</div>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: `${s.color}18`, border: `1px solid ${s.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.Icon size={14} color={s.color} />
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: s.color, fontFamily: T.mono, letterSpacing: '-1px', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── TOGGLE ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 6, marginBottom: 14, display: 'flex', gap: 6 }}>
        <button className={`ef-tab ${viewMode === 'log' ? 'ef-tab-active' : 'ef-tab-inactive'}`} onClick={() => resetView(setViewMode)('log')}>
          <Activity size={15} /> Daily Shifts
        </button>
        <button className={`ef-tab ${viewMode === 'operators' ? 'ef-tab-active' : 'ef-tab-inactive'}`} onClick={() => resetView(setViewMode)('operators')}>
          <Users size={15} /> Operator Summary
        </button>
        <button className={`ef-tab ${viewMode === 'activities' ? 'ef-tab-active' : 'ef-tab-inactive'}`} onClick={() => resetView(setViewMode)('activities')}>
          <BarChart3 size={15} /> Activity Summary
        </button>
      </div>

      {/* ── FILTERS ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select className="ef-select" value={opFilter} onChange={e => resetView(setOpFilter)(e.target.value)}>
          <option value="all">All Operators</option>
          {OPERATORS.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
        </select>
        <select className="ef-select" value={actFilter} onChange={e => resetView(setActFilter)(e.target.value)}>
          <option value="all">All Activities</option>
          {ACTIVITY_NAMES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="ef-select" value={dayFilter} onChange={e => resetView(setDayFilter)(e.target.value)}>
          <option value="all">All of September</option>
          {days.map(d => <option key={d} value={d}>{fmtDate(new Date(2026, 8, d))}</option>)}
        </select>
        {(opFilter !== 'all' || actFilter !== 'all' || dayFilter !== 'all') && (
          <button className="ef-select" onClick={() => { setOpFilter('all'); setActFilter('all'); setDayFilter('all'); setPage(1); setExpanded(null); }}>
            Clear filters
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.muted }}>Click any row to see the full shift breakdown</span>
      </div>

      {/* ── DAILY SHIFTS ── */}
      {viewMode === 'log' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <SectionHeader Icon={Activity} color={T.blue} title="Daily Shifts" sub="Sep 1 – Sep 30, 2026 · 8h each" />
          <Legend />
          {filtered.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: T.muted }}>No shifts match these filters</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th className="ef-th" style={{ paddingLeft: 20 }}>Field Operator</th>
                      <th className="ef-th">Client &amp; Site</th>
                      <th className="ef-th">Date &amp; Shift</th>
                      <th className="ef-th">Active Work</th>
                      <th className="ef-th">Shift Split</th>
                      <th className="ef-th">Efficiency</th>
                      <th className="ef-th" style={{ width: 30 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(r => {
                      const open = expanded === r.id;
                      return (
                        <React.Fragment key={r.id}>
                          <tr className={`ef-tr ${open ? 'ef-tr-open' : ''}`} onClick={() => toggle(r.id)}>
                            <td className="ef-td" style={{ paddingLeft: 20 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <UserAvatar name={r.operator} size={28} />
                                <div>
                                  <div style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>{r.operator}</div>
                                  <div style={{ fontSize: 11, color: T.muted }}>{r.operatorId} · {r.dept}</div>
                                </div>
                              </div>
                            </td>
                            <td className="ef-td">
                              <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 3 }}>{r.client}</div>
                              <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={10} /> {r.site}</div>
                            </td>
                            <td className="ef-td">
                              <div style={{ fontSize: 12, color: T.text, fontWeight: 600, fontFamily: T.mono, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Calendar size={10} /> {fmtDate(r.start)}
                              </div>
                              <div style={{ fontFamily: T.mono, fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Clock size={10} color={T.muted} />
                                <span style={{ color: T.green }}>{fmtTime(r.start)}</span>
                                <span style={{ color: T.muted }}>→</span>
                                <span style={{ color: T.red }}>{fmtTime(r.end)}</span>
                              </div>
                            </td>
                            <td className="ef-td">
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                                {r.activities.map(a => <ActivityBadge key={a} name={a} small />)}
                              </div>
                              <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{r.tActive} min active · {r.tIdle} min idle</div>
                            </td>
                            <td className="ef-td"><ShiftBar data={r} width={150} /></td>
                            <td className="ef-td"><EffPill value={r.efficiency} /></td>
                            <td className="ef-td"><Chevron open={open} /></td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={7} style={{ padding: 0 }}>
                                <DetailPanel
                                  data={r}
                                  worker={r.operator}
                                  job={`${r.client} — ${r.site} · ${fmtDate(r.start)}, ${fmtTime(r.start)}–${fmtTime(r.end)}`}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager page={page} pages={totalPages} total={filtered.length} perPage={rowsPerPage} onChange={(p) => { setPage(p); setExpanded(null); }} />
            </>
          )}
        </div>
      )}

      {/* ── OPERATOR SUMMARY ── */}
      {viewMode === 'operators' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <SectionHeader Icon={Users} color={T.teal} title="Operator Efficiency Summary" sub="Ranked · September 2026" />
          <Legend />
          {operatorSummary.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: T.muted }}>No shifts match these filters</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="ef-th" style={{ paddingLeft: 20 }}>#</th>
                    <th className="ef-th">Field Operator</th>
                    <th className="ef-th" style={{ textAlign: 'center' }}>Shifts</th>
                    <th className="ef-th" style={{ textAlign: 'center' }}>Active Hours</th>
                    <th className="ef-th" style={{ textAlign: 'center' }}>Idle Hours</th>
                    <th className="ef-th">Shift Split</th>
                    <th className="ef-th">Efficiency</th>
                    <th className="ef-th" style={{ width: 30 }} />
                  </tr>
                </thead>
                <tbody>
                  {operatorSummary.map((op, i) => {
                    const open = expanded === op.id;
                    return (
                      <React.Fragment key={op.id}>
                        <tr className={`ef-tr ${open ? 'ef-tr-open' : ''}`} onClick={() => toggle(op.id)}>
                          <td className="ef-td" style={{ paddingLeft: 20, fontFamily: T.mono, fontWeight: 800, color: i < 3 ? T.amber : T.muted }}>{i + 1}</td>
                          <td className="ef-td">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                              <UserAvatar name={op.name} size={30} />
                              <div>
                                <div style={{ fontWeight: 700, color: T.text, fontSize: 13, marginBottom: 3 }}>{op.name}</div>
                                <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{op.id} · {op.dept}</div>
                              </div>
                            </div>
                          </td>
                          <td className="ef-td" style={{ textAlign: 'center' }}><span style={{ fontSize: 15, fontWeight: 800, color: T.blue, fontFamily: T.mono }}>{op.count}</span></td>
                          <td className="ef-td" style={{ textAlign: 'center' }}><span style={{ fontSize: 15, fontWeight: 800, color: T.green, fontFamily: T.mono }}>{fmtMin(op.tActive)}</span></td>
                          <td className="ef-td" style={{ textAlign: 'center' }}><span style={{ fontSize: 13, fontWeight: 800, color: T.muted, fontFamily: T.mono }}>{fmtMin(op.tIdle)}</span></td>
                          <td className="ef-td"><ShiftBar data={op} width={150} /></td>
                          <td className="ef-td"><EffPill value={op.efficiency} big /></td>
                          <td className="ef-td"><Chevron open={open} /></td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={8} style={{ padding: 0 }}>
                              <DetailPanel
                                data={op}
                                worker={op.name}
                                job={`September 2026 · ${op.count} shifts × ${SHIFT_MIN} min`}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ACTIVITY SUMMARY ── */}
      {viewMode === 'activities' && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <SectionHeader Icon={BarChart3} color={T.purple} title="Activity Summary" sub="Time inside Active Work · September 2026" />
          {activitySummary.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: T.muted }}>No shifts match these filters</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th className="ef-th" style={{ paddingLeft: 20 }}>Activity</th>
                    <th className="ef-th" style={{ textAlign: 'center' }}>Shifts</th>
                    <th className="ef-th" style={{ textAlign: 'center' }}>Operators</th>
                    <th className="ef-th" style={{ textAlign: 'center' }}>Total Time</th>
                    <th className="ef-th" style={{ textAlign: 'center' }}>Avg / Shift</th>
                    <th className="ef-th">Shift Efficiency</th>
                    <th className="ef-th" style={{ width: 30 }} />
                  </tr>
                </thead>
                <tbody>
                  {activitySummary.map(a => {
                    const open = expanded === a.activity;
                    return (
                      <React.Fragment key={a.activity}>
                        <tr className={`ef-tr ${open ? 'ef-tr-open' : ''}`} onClick={() => toggle(a.activity)}>
                          <td className="ef-td" style={{ paddingLeft: 20 }}><ActivityBadge name={a.activity} /></td>
                          <td className="ef-td" style={{ textAlign: 'center' }}><span style={{ fontSize: 15, fontWeight: 800, color: T.blue, fontFamily: T.mono }}>{a.count}</span></td>
                          <td className="ef-td" style={{ textAlign: 'center' }}><span style={{ fontSize: 15, fontWeight: 800, color: T.cyan, fontFamily: T.mono }}>{a.operators}</span></td>
                          <td className="ef-td" style={{ textAlign: 'center' }}><span style={{ fontSize: 14, fontWeight: 800, color: T.green, fontFamily: T.mono }}>{fmtMin(a.activityMin)}</span></td>
                          <td className="ef-td" style={{ textAlign: 'center' }}><span style={{ fontSize: 13, fontWeight: 800, color: T.amber, fontFamily: T.mono }}>{Math.round(a.activityMin / a.count)} min</span></td>
                          <td className="ef-td"><EffPill value={a.efficiency} big /></td>
                          <td className="ef-td"><Chevron open={open} /></td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0 }}>
                              <DetailPanel
                                data={a}
                                worker={`${a.operators} operators`}
                                job={`All shifts that included ${a.activity} · ${a.count} × ${SHIFT_MIN} min`}
                                showName={false}
                                extra={
                                  <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Time on {a.activity} by operator</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
                                      {a.byOperator.map(op => (
                                        <div key={op.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                          <UserAvatar name={op.name} size={30} />
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{op.name}</div>
                                            <div style={{ fontSize: 10, color: T.muted, fontFamily: T.mono }}>{a.activity}: {op.activityMin} min · {op.count} shifts</div>
                                          </div>
                                          <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 13, color: effColor(op.efficiency) }}>{op.efficiency.toFixed(1)}%</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                }
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EfficiencyDataDashboard;
